import React from "react";
import { Check, AlertTriangle, Info, UserCheck, ClipboardCheck } from "lucide-react";
import { Reveal, SectionLabel, ExternalLinkIcon } from "./primitives";
import { ivaFeatures, ivaConsiderations, ipRole, businessDetails } from "../data/content";
import { track } from "../lib/analytics";

const roleIcons = [UserCheck, ClipboardCheck];

export const IvaSection = () => (
  <section
    id="understanding-iva"
    aria-labelledby="iva-heading"
    className="border-t border-brand-border bg-white py-16 lg:py-24"
  >
    <div className="mx-auto max-w-content px-4 lg:px-8">
      <Reveal className="max-w-3xl">
        <SectionLabel>Understanding your IVA</SectionLabel>
        <h2
          id="iva-heading"
          className="mt-3 font-heading text-3xl font-bold tracking-tight text-brand-navy lg:text-4xl"
        >
          Understanding an Individual Voluntary Arrangement
        </h2>
        <div className="mt-5 space-y-4 text-lg leading-relaxed text-brand-body">
          <p>
            An Individual Voluntary Arrangement, usually called an IVA, is a formal and legally
            binding agreement between you and your creditors.
          </p>
          <p>
            An IVA allows an agreed amount to be paid towards qualifying debts, normally through
            regular payments over an agreed period. It must be proposed and supervised by a
            Licensed Insolvency Practitioner.
          </p>
        </div>
        <div
          className="mt-6 flex gap-3 rounded-xl border border-brand-amber/40 bg-brand-amberSoft p-4"
          data-testid="iva-risk-summary"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-amber" aria-hidden="true" />
          <p className="text-base font-semibold leading-relaxed text-brand-navy">
            An IVA is a form of insolvency. It isn’t right for everyone, it will affect your
            credit rating, and fees apply.
          </p>
        </div>
      </Reveal>

      {/* The role of your Insolvency Practitioner: nominee and supervisor */}
      <Reveal className="mt-10 rounded-2xl border border-brand-border bg-brand-navy p-7 text-white lg:p-9">
        <h3 className="font-heading text-xl font-bold text-white lg:text-2xl">
          The role of your Insolvency Practitioner
        </h3>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-white/80">
          {ipRole.intro}
        </p>
        <div className="mt-7 grid gap-5 md:grid-cols-2">
          {ipRole.roles.map((role, i) => {
            const Icon = roleIcons[i % roleIcons.length];
            return (
              <div
                key={role.title}
                className="rounded-xl border border-white/10 bg-white/[0.04] p-6"
                data-testid={`ip-role-${i}`}
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-teal/20 text-brand-teal">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h4 className="mt-4 font-heading text-lg font-bold text-white">{role.title}</h4>
                <p className="mt-2 text-base leading-relaxed text-white/80">{role.body}</p>
              </div>
            );
          })}
        </div>
      </Reveal>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <Reveal className="rounded-2xl border border-brand-border bg-brand-bg p-7 lg:p-8">
          <h3 className="flex items-center gap-2 font-heading text-xl font-bold text-brand-navy">
            <Check className="h-5 w-5 text-brand-teal" aria-hidden="true" />
            Potential features of an IVA
          </h3>
          <ul className="mt-5 space-y-3">
            {ivaFeatures.map((item) => (
              <li key={item} className="flex gap-3 text-base leading-relaxed text-brand-body">
                <Check className="mt-1 h-4 w-4 flex-shrink-0 text-brand-teal" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.1} className="rounded-2xl border border-brand-border bg-brand-bg p-7 lg:p-8">
          <h3 className="flex items-center gap-2 font-heading text-xl font-bold text-brand-navy">
            <AlertTriangle className="h-5 w-5 text-brand-amber" aria-hidden="true" />
            Important considerations
          </h3>
          <ul className="mt-5 space-y-3">
            {ivaConsiderations.map((item) => (
              <li key={item} className="flex gap-3 text-base leading-relaxed text-brand-body">
                <span
                  className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand-amber"
                  aria-hidden="true"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>

      <Reveal
        className="mt-8 flex gap-3 rounded-2xl border border-brand-amber/40 bg-brand-amberSoft p-5"
        as="div"
      >
        <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-amber" aria-hidden="true" />
        <p className="text-base leading-relaxed text-brand-navy">
          The information on this website is general information, provided in contemplation of an
          insolvency appointment, and should not be treated as personal financial or legal
          advice. The suitability and effect of an IVA depend on your individual circumstances.
        </p>
      </Reveal>

      <Reveal className="mt-6 flex flex-col gap-5 rounded-2xl border border-brand-border border-l-4 border-l-[#1B1FB5] bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-5">
          <img
            src="/moneyhelper.png"
            alt="MoneyHelper"
            width="800"
            height="520"
            className="h-11 w-auto flex-shrink-0 sm:h-14"
          />
          <p className="text-base leading-relaxed text-brand-body">
            <span className="font-heading font-bold text-brand-navy">Looking for free debt advice?</span>{" "}
            To find other sources of free advice visit MoneyHelper. It’s here to listen and give
            free, impartial, trusted guidance.
          </p>
        </div>
        <a
          href={businessDetails.moneyHelperUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("external_regulator_link_clicked", { label: "moneyhelper" })}
          className="inline-flex min-h-[48px] flex-shrink-0 items-center justify-center gap-2 rounded-full bg-[#C6168D] px-5 text-center font-semibold text-white transition-colors hover:bg-[#A00F71]"
          data-testid="moneyhelper-link"
          aria-label="Visit MoneyHelper for free debt advice — opens in a new tab"
        >
          Visit MoneyHelper <ExternalLinkIcon />
        </a>
      </Reveal>

    </div>
  </section>
);
